import { hideUploadProgress, showUploadProgress } from "@/store/app/app.slice";
import { setRemoveFile } from "@/store/upload/upload.slice";
import { useEffect, useMemo, useState, useRef } from "react";
import { ProgressBar } from "..";
import { RootState } from "@/store/index";
import { connect, ConnectedProps } from "react-redux";
import styles from "./uploadProgress.module.scss";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getFileFromResumableFiles,
  removeFileFromResumableFiles,
} from "./utils";
import { updateMediaStatus } from "@/store/media/media.slice";
import { SquarePen, X } from "lucide-react";
import { AlertDialog } from "@/utils/ui/AlertDialog/AlertDialog";

const formatDuration = (seconds: number) => {
  if (seconds < 60) return `${Math.ceil(seconds)} seconds`;
  if (seconds < 3600) return `${Math.ceil(seconds / 60)} minutes`;
  return `${Math.ceil(seconds / 3600)} hours`;
};

const formatBytes = (bytes: number) => {
  if (!bytes || bytes <= 0) return "0 MB";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const exp = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1
  );
  const val = bytes / Math.pow(1024, exp);
  return `${val.toFixed(exp === 0 ? 0 : 1)} ${units[exp]}`;
};

const UploadProgress = ({
  files,
  isUploadProgressOpen,
  isModalOpen,
  setRemoveFile,
  updateMediaStatus,
  id,
}: Props) => {
  const navigateTo = useNavigate();
  const location = useLocation();
  const [isIndividualFileProgressOpen, setIsIndividualFileProgressOpen] =
    useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [fileToCancel, setFileToCancel] = useState<any>(null);
  const [cancelledFiles, setCancelledFiles] = useState<Set<string>>(new Set());
  const uploadSpeeds = useRef<
    Record<
      string,
      { bytesUploaded: number; startTime: number; lastUpdate: number }
    >
  >({});
  const individualFilesScrollRef = useRef<HTMLDivElement | null>(null);

  const filesInProgress = useMemo(() => {
    return Object.values(files).filter(({ file }) => {
      return file.error === null;
    });
  }, [files]);

  // Identifiers only (not progress) - re-running the scroll effect on every
  // progress tick was yanking the user back to the bottom while they were
  // looking at an earlier file. This should only fire when a file is added
  // or removed from the list.
  const scrollKey = useMemo(() => {
    return filesInProgress.map(({ file }) => file.identifier).join("|");
  }, [filesInProgress]);

  useEffect(() => {
    if (!isUploadProgressOpen) return;
    if (!isIndividualFileProgressOpen) return;
    const raf = requestAnimationFrame(() => {
      const el = individualFilesScrollRef.current;
      if (!el) return;
      el.scrollTop = el.scrollHeight - el.clientHeight;
    });
    return () => cancelAnimationFrame(raf);
  }, [isUploadProgressOpen, isIndividualFileProgressOpen, scrollKey]);

  const calculateSecondsRemaining = (file: any): number | null => {
    if (!file || !file.size || !file.progress) return null;

    const fileId = file.identifier;
    const currentTime = Date.now();
    const currentProgress = file.progress;
    const totalBytes = file.size;
    const uploadedBytes = (totalBytes * currentProgress) / 100;

    if (!uploadSpeeds.current[fileId]) {
      uploadSpeeds.current[fileId] = {
        bytesUploaded: uploadedBytes,
        startTime: currentTime,
        lastUpdate: currentTime,
      };
      return null;
    }

    const speedData = uploadSpeeds.current[fileId];
    const timeElapsed = (currentTime - speedData.lastUpdate) / 1000; // in seconds
    const bytesUploadedSinceLastUpdate =
      uploadedBytes - speedData.bytesUploaded;
    const uploadSpeed = bytesUploadedSinceLastUpdate / timeElapsed; // bytes per second

    speedData.bytesUploaded = uploadedBytes;
    speedData.lastUpdate = currentTime;

    if (uploadSpeed <= 0) return null;

    const remainingBytes = totalBytes - uploadedBytes;
    return remainingBytes / uploadSpeed;
  };

  const calculateTotalTimeRemaining = () => {
    // Files upload in parallel, so the overall estimate is the slowest
    // file's remaining time, not the sum.
    let maxSeconds = 0;
    let totalProgress = 0;
    let totalFiles = 0;

    filesInProgress.forEach(({ file }) => {
      if (!file.isUploadedSuccessfully) {
        const secondsRemaining = calculateSecondsRemaining(file);
        if (secondsRemaining !== null) {
          maxSeconds = Math.max(maxSeconds, secondsRemaining);
        }
        totalProgress += file.progress;
        totalFiles++;
      }
    });

    if (totalFiles === 0) return { time: "0 seconds", progress: 0 };

    const averageProgress = totalProgress / totalFiles;

    if (maxSeconds === 0)
      return { time: "0 seconds", progress: averageProgress };

    return { time: formatDuration(maxSeconds), progress: averageProgress };
  };

  // Hide if on MediaDetail page
  if (location.pathname.includes(`/media/${id}`)) return null;

  // Only hide if there are no files in progress
  if (!Object.values(filesInProgress).length) return null;

  const handleProgressBoxToggle = () => {
    setIsIndividualFileProgressOpen((prev) => !prev);
  };

  const sendCancelUploadRequest = (fileId: string) => {
    if (!fileId) return;
    const apiUrl = process.env.NX_APP_MEDIA_CANCEL ?? "";
    const formData = new FormData();
    formData.append("status", "-2");
    formData.append("id", fileId);
    updateMediaStatus({ id: fileId, status: -2 });
    const sent = navigator.sendBeacon(apiUrl, formData);
  };

  const handleFileAction = (file: any) => {
    if (file.progress === 100) {
      const fileIdentifier = Object.keys(files).find(
        (key) => files[key].file.id === file.id
      );
      if (fileIdentifier) {
        setRemoveFile({ identifier: fileIdentifier });
      }
      return;
    }
    if (!file.isUploadedSuccessfully && !cancelledFiles.has(file.id)) {
      setFileToCancel(file);
      setShowConfirmModal(true);
    }
  };

  const handleConfirmCancel = () => {
    if (fileToCancel) {
      const fileToDelete = getFileFromResumableFiles(
        fileToCancel.resumableIdentifier
      );
      if (fileToDelete) {
        fileToDelete.cancel();
        removeFileFromResumableFiles(fileToCancel.resumableIdentifier);
      }
      const fileIdentifier = Object.keys(files).find(
        (key) => files[key].file.id === fileToCancel.id
      );
      if (fileIdentifier) {
        setRemoveFile({ identifier: fileIdentifier });
      }
      sendCancelUploadRequest(fileToCancel.id);
      setShowConfirmModal(false);
      setFileToCancel(null);
    }
  };

  const handleCancel = () => {
    setShowConfirmModal(false);
    setFileToCancel(null);
  };

  const renderProgressHeader = () => {
    const total = filesInProgress.length;
    const completedCount = filesInProgress.filter(
      ({ file }) => file.isUploadedSuccessfully
    ).length;
    const allDone = completedCount === total;

    // Overall progress = average across all files (0–100 each)
    const overallProgress =
      total === 0
        ? 0
        : Math.round(
            filesInProgress.reduce((sum, { file }) => sum + file.progress, 0) /
              total
          );

    const { time } = calculateTotalTimeRemaining();
    const showEta =
      !isIndividualFileProgressOpen && time !== "0 seconds" && !allDone;

    return (
      <div className="d-flex flex-column w-100 position-relative">
        {/* Top row: label + completed/total pill + eta + percentage + toggle */}
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2 f-13">
            <span>{allDone ? "Uploaded Successfully" : "Uploading"}</span>
            <span
              style={{
                background: "var(--bg-hover-color)",
                color: "var(--bs-active-color)",
                borderRadius: 10,
                padding: "1px 8px",
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              {completedCount} / {total}
            </span>
            {showEta && (
              <>
                <span style={{ color: "var(--bs-border-color)" }}>|</span>
                <span style={{ color: "var(--bs-secondary-color)" }}>
                  {time}
                </span>
              </>
            )}
          </div>
          <div className="d-flex align-items-center gap-2">
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: "var(--bs-active-color)",
              }}
            >
              {overallProgress}%
            </span>
            <span
              onClick={handleProgressBoxToggle}
              style={{ cursor: "pointer" }}
            >
              <i
                className={`bi ${
                  isIndividualFileProgressOpen
                    ? "bi-caret-down-fill"
                    : "bi-caret-up-fill"
                }`}
                style={{ color: "var(--bs-secondary-color)" }}
              />
            </span>
          </div>
        </div>
        {/* Overall progress bar — always visible in header */}
        <div style={{ bottom: '-10px' }} className="position-absolute left-0 w-100">
          <ProgressBar progress={overallProgress} />
        </div>
      </div>
    );
  };

  const renderIndividualFileProgress = () =>
    Object.values(filesInProgress).map(({ file }) => {
      const safeProgress = Math.min(Math.max(file.progress, 0), 100);
      const progressLabel =
        safeProgress > 0 && safeProgress < 10
          ? safeProgress.toFixed(1)
          : Math.round(safeProgress).toString();
      const isPreparing = file.uploadPhase === "preparing";
      const fileSize = (file as { size?: number }).size;
      const uploadedBytes = fileSize ? (fileSize * safeProgress) / 100 : 0;
      return (
        <div
          className={`${styles.fileProgress} p-2 position-relative border-bottom`}
          key={file.identifier}
          style={{ backgroundColor: "white" }}
        >
          <div className="d-flex justify-content-between align-items-center py-2">
            <div className="d-flex align-items-center gap-2" style={{ width: "200px" }}>
              <SquarePen
                size={16}
                className={file.id ? "pointer" : "text-muted"}
                onClick={() => file.id && navigateTo(`/media/${file.id}`)}
              />
              <span
                className={`fw-medium text-truncate ${file.id ? "pointer" : ""}`}
                onClick={() => file.id && navigateTo(`/media/${file.id}`)}>
                {file.name}
              </span>
            </div>
            <div className="d-flex gap-1 align-items-center">
              {isPreparing ? (
                <span className="text-muted f-12">Preparing upload…</span>
              ) : (
                <>
                  {!!fileSize && (
                    <span className="text-muted f-12">
                      {formatBytes(uploadedBytes)} / {formatBytes(fileSize)}
                    </span>
                  )}
                  <span className="f-12">({progressLabel}%)</span>
                </>
              )}
              {file?.type !== "gdrive" && !isPreparing && (
                <X
                  size={16}
                  className="pointer"
                  onClick={() => handleFileAction(file)}
                />
              )}
            </div>
          </div>
          <div
            className="position-absolute bottom-0 left-0"
            style={{ width: "calc(100% - 20px)" }}
          >
            <ProgressBar progress={file.progress} />
          </div>
        </div>
      );
    });

  return (
    <>
      <div
        className={`${styles.uploadBox} shadow position-fixed border`}
        style={{
          zIndex: 1000,
          backgroundColor: "white",
          display: isModalOpen ? "none" : "block",
          opacity: isUploadProgressOpen ? 1 : 0,
          visibility: isUploadProgressOpen ? "visible" : "hidden",
          transition: "opacity 0.3s ease-in-out",
          maxWidth: '500px'
        }}
      >
        <div className={styles.uploadHeader}>{renderProgressHeader()}</div>
        {isIndividualFileProgressOpen && (
          <div
            ref={individualFilesScrollRef}
            className={styles.individualFilesScroll}
          >
            {renderIndividualFileProgress()}
          </div>
        )}
      </div>
      {showConfirmModal && (
        <AlertDialog
          isOpen={showConfirmModal}
          onClose={handleCancel}
          onConfirm={handleConfirmCancel}
          title="Unsaved Changes"
          message="Your file upload is in progress. Are you sure you want to cancel?"
          confirmText="Yes, leave"
          cancelText="No, stay"
          confirmButtonType="warning"
        />
      )}
    </>
  );
};

const mapState = (state: RootState) => ({
  files: state.upload.files,
  isUploadProgressOpen: state.app.isUploadProgressOpen,
  isModalOpen: state.app.isModalOpen,
  id: state.media.mediaDetails.media.id,
});

const mapDispatch = {
  showUploadProgress,
  hideUploadProgress,
  setRemoveFile,
  updateMediaStatus,
};

const connector = connect(mapState, mapDispatch);
type Props = ConnectedProps<typeof connector>;

export default connector(UploadProgress);
